import { useEffect } from 'react';
import { useFeedQuery } from '@/features/feed/api';
import { FeedPostCard } from '@/features/feed/ui';
import { useCurrentUser } from '@/features/user';
import { useInfiniteScroll } from '@/hooks';
import { flattenInfiniteQueryData } from '@/lib';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components';
import { Loader2 } from 'lucide-react';
import type { FeedWidgetProps } from './types';

/**
 * Виджет ленты активности
 * Композирует посты из разных источников (друзья + избранные вишлисты)
 * Поддерживает бесконечный скролл с cursor-пагинацией
 */
export function FeedWidget({
  onItemClick,
  onUserClick,
  onRefreshReady,
}: FeedWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFeedQuery({ userId: currentUser?.id });

  // Передаём refetch наверх в FeedPage чтобы PullToRefresh мог его вызвать
  useEffect(() => {
    onRefreshReady?.(() => refetch());
  }, [refetch, onRefreshReady]);

  // Триггер бесконечного скролла
  const scrollTriggerRef = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  });

  // Извлекаем посты из всех загруженных страниц
  const posts = flattenInfiniteQueryData(data);

  // Состояние загрузки (первая страница)
  // flex-1 + overflow-hidden — скелетон занимает всё оставшееся место под шапкой
  // и обрезается по нижней границе экрана, не давая скроллить пустоту
  if (isLoading) {
    return (
      <div className="space-y-4 flex-1 overflow-hidden">
        {/* Карточка 1 */}
        <div className="rounded-[var(--radius)] bg-muted animate-pulse py-4">
          <div className="flex items-center gap-3.5 mb-4 px-4">
            <div className="size-12 rounded-full bg-background flex-shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3.5 w-44 rounded-md bg-background" />
              <div className="h-3 w-20 rounded-md bg-background" />
            </div>
          </div>
          <div className="mx-4 aspect-[4/3] rounded-[var(--radius)] bg-background" />
        </div>

        {/* Карточка 2 */}
        <div className="rounded-[var(--radius)] bg-muted animate-pulse py-4">
          <div className="flex items-center gap-3.5 mb-4 px-4">
            <div className="size-12 rounded-full bg-background flex-shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3.5 w-40 rounded-md bg-background" />
              <div className="h-3 w-16 rounded-md bg-background" />
            </div>
          </div>
          <div className="mx-4 aspect-[4/3] rounded-[var(--radius)] bg-background" />
        </div>

        {/* Карточка 3 */}
        <div className="rounded-[var(--radius)] bg-muted animate-pulse py-4">
          <div className="flex items-center gap-3.5 mb-4 px-4">
            <div className="size-12 rounded-full bg-background flex-shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3.5 w-36 rounded-md bg-background" />
              <div className="h-3 w-24 rounded-md bg-background" />
            </div>
          </div>
          <div className="mx-4 aspect-[4/3] rounded-[var(--radius)] bg-background" />
        </div>
      </div>
    );
  }

  // Ошибка загрузки
  if (error) {
    console.error('FeedWidget error:', error.message, error);
    return (
      <div className="text-center py-12 px-4">
        <p className="text-muted-foreground">{t('common:error')}</p>
        <p className="text-xs text-muted-foreground/60 mt-2 break-all px-4">{error.message}</p>
      </div>
    );
  }

  // Пустая лента
  if (posts.length === 0) {
    return (
      <EmptyState
        imageSrc="https://bxrqypmvsqqysoztyzxo.supabase.co/storage/v1/object/public/assets/Frame%206.svg"
        title={t('feed:feed.emptyFeed')}
        description={t('feed:feed.emptyFeedDescription')}
        imageSize="small"
        className="flex-1 pb-20"
      />
    );
  }

  // Лента с постами + бесконечный скролл
  return (
    <div>
      <div className="space-y-4">
        {posts.map((post) => (
          <FeedPostCard
            key={post.id}
            post={post}
            onItemClick={onItemClick}
            onUserClick={onUserClick}
          />
        ))}
      </div>

      {/* Индикатор загрузки следующей страницы */}
      {isFetchingNextPage && (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Невидимый триггер бесконечного скролла */}
      <div ref={scrollTriggerRef} />
    </div>
  );
}